import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Link2,
  Loader2,
  MapPin,
  Plus,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { Input, InputGroup } from '@/components/ui/catalyst/input';
import { Select } from '@/components/ui/catalyst/select';
import { Textarea } from '@/components/ui/catalyst/textarea';
import { Z_INDEX_MODAL_BASE } from '@/components/ui/zIndex';
import { useProviderResolveSearch } from '@/hooks/useProviderResolveSearch';
import { toTitleCase } from '@/lib/strings';
import {
  enhancedApi as providerDirectoryV2GooglePlacesApi,
  type GooglePlacesAutocompletePredictionV2,
} from '@/services/api/generated/providerDirectoryV2GooglePlacesService';
import {
  type ProviderDedupCandidate,
  type ProviderDirectoryCreateConflictResponseV2,
  useProviderDirectoryV2CreateServiceMutation,
  useProviderDirectoryV2DirectoryCreateServiceMutation,
} from '@/services/api/generated/providerDirectoryV2IdentityService';
import {
  medicalProvidersApiClient,
  useApproveMedicalProviderMutation,
  useUpdateMedicalProviderMutation,
} from '@/services/api/treatmentService';
import type { ReviewFoundProviderEntry } from '@/services/types/treatment-types';
import {
  ProviderTreatmentStatus,
  TreatmentCareLevel,
} from '@/services/types/treatment-types';
import {
  compactInternalLocation,
  compactLocation,
} from '@/utils/addressFormatting';
import {
  composeAddress,
  EMPTY_DRAFT,
  firstNonEmpty,
  parseAddressComponents,
  type ProviderDraft,
  type ResolveProviderMatch,
  toProviderTypeHint,
} from '@/utils/providerDraft';
import { validatePhoneNumber } from '@/utils/validation';

import { ProviderTypeSelector } from './ProviderTypeSelector';
import { RequestTemplatePanel } from './RequestTemplatePanel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AddProviderModalV2Props {
  isOpen: boolean;
  onClose: () => void;
  matterId?: string;
  onProviderCreated: (id: string, providerName?: string) => void;
  centerLat?: number;
  centerLng?: number;
}

type WizardStep = 'search' | 'draft' | 'conflict' | 'template';

/** Type guard for RTK Query 409 conflict errors. */
function isConflictError(
  error: unknown,
): error is { status: 409; data: ProviderDirectoryCreateConflictResponseV2 } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { status: number }).status === 409 &&
    'data' in error &&
    typeof (error as { data: unknown }).data === 'object' &&
    (error as { data: { candidates?: unknown } }).data !== null &&
    Array.isArray(
      (error as { data: { candidates: unknown[] } }).data.candidates,
    )
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AddProviderModalV2 = ({
  isOpen,
  onClose,
  matterId,
  onProviderCreated,
  centerLat,
  centerLng,
}: AddProviderModalV2Props) => {
  const dispatch = useDispatch();

  // Wizard state
  const [step, setStep] = useState<WizardStep>('search');

  // 3-source search
  const search = useProviderResolveSearch({ matterId, centerLat, centerLng });

  // Show-all toggles for grouped sections
  const [showAllInternal, setShowAllInternal] = useState(false);
  const [showAllReview, setShowAllReview] = useState(false);
  const [showAllGoogle, setShowAllGoogle] = useState(false);

  // Draft form
  const [draft, setDraft] = useState<ProviderDraft>(EMPTY_DRAFT);
  const [providerType, setProviderType] = useState<'hospital' | 'clinic' | null>(null);
  const [treatmentStatus, setTreatmentStatus] = useState<ProviderTreatmentStatus | null>(null);
  const [careLevel, setCareLevel] = useState<TreatmentCareLevel | null>(null);
  const [selectedReviewForApproval, setSelectedReviewForApproval] =
    useState<ReviewFoundProviderEntry | null>(null);

  // Conflict state (409 candidates)
  const [conflictCandidates, setConflictCandidates] = useState<ProviderDedupCandidate[]>([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [createAnywayReason, setCreateAnywayReason] = useState('');
  const [showCreateAnywayForm, setShowCreateAnywayForm] = useState(false);

  // Phone validation
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // Address autocomplete (Google Places) for manual address entry
  const [addressPredictions, setAddressPredictions] = useState<GooglePlacesAutocompletePredictionV2[]>([]);
  const [showAddressPredictions, setShowAddressPredictions] = useState(false);
  const addressTimerRef = useRef<number | null>(null);
  const addressSessionTokenRef = useRef(crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);
  const [triggerAddressAutocomplete] =
    providerDirectoryV2GooglePlacesApi.endpoints.providerDirectoryV2GooglePlacesAutocomplete.useLazyQuery();

  // Auto-query address predictions from provider name
  const [nameAutoQueryPredictions, setNameAutoQueryPredictions] = useState<GooglePlacesAutocompletePredictionV2[]>([]);
  const [isAutoQueryLoading, setIsAutoQueryLoading] = useState(false);
  const [showManualAddressEntry, setShowManualAddressEntry] = useState(false);
  const [selectedAutoQueryPlaceId, setSelectedAutoQueryPlaceId] = useState<string | null>(null);
  const autoQueryRanRef = useRef(false);

  // Track whether the last create/reuse was a reuse (for toast in handleFinish)
  const [lastReusedExisting, setLastReusedExisting] = useState<boolean | null>(null);

  // Created service ID (after identity resolved or created)
  const [createdServiceId, setCreatedServiceId] = useState<string | null>(null);
  const [createdMedicalProviderId, setCreatedMedicalProviderId] = useState<string | null>(null);
  const [createdServiceName, setCreatedServiceName] = useState<string>('');

  // Mutations
  const [createMatterProvider, { isLoading: isCreatingMatterProvider }] =
    useProviderDirectoryV2CreateServiceMutation();
  const [createDirectoryProvider, { isLoading: isCreatingDirectoryProvider }] =
    useProviderDirectoryV2DirectoryCreateServiceMutation();
  const [approveMedicalProvider, { isLoading: isApprovingReviewProvider }] =
    useApproveMedicalProviderMutation();
  const [updateMedicalProvider] = useUpdateMedicalProviderMutation();
  const [googleDetails, { isFetching: isFetchingDetails }] = providerDirectoryV2GooglePlacesApi.endpoints.providerDirectoryV2GooglePlacesDetails.useLazyQuery();

  // Need a ref so the handleGooglePick callback sees the latest token
  const sessionTokenRef = useRef(search.googleSessionToken);
  useEffect(() => {
    sessionTokenRef.current = search.googleSessionToken;
  }, [search.googleSessionToken]);

  const isDirectoryMode = !matterId;

  // ---------------------------------------------------------------------------
  // Reset on open
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) {
      // Clean up address autocomplete debounce timer on close
      if (addressTimerRef.current) {
        window.clearTimeout(addressTimerRef.current);
        addressTimerRef.current = null;
      }
      return;
    }
    setStep('search');
    search.clear();
    setShowAllInternal(false);
    setShowAllReview(false);
    setShowAllGoogle(false);
    setDraft(EMPTY_DRAFT);
    setProviderType(null);
    setTreatmentStatus(null);
    setCareLevel(null);
    setSelectedReviewForApproval(null);
    setConflictCandidates([]);
    setSelectedCandidateId(null);
    setCreateAnywayReason('');
    setShowCreateAnywayForm(false);
    setPhoneError(null);
    setAddressPredictions([]);
    setShowAddressPredictions(false);
    addressSessionTokenRef.current = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
    setNameAutoQueryPredictions([]);
    setIsAutoQueryLoading(false);
    setShowManualAddressEntry(false);
    setSelectedAutoQueryPlaceId(null);
    autoQueryRanRef.current = false;
    setLastReusedExisting(null);
    setCreatedServiceId(null);
    setCreatedMedicalProviderId(null);
    setCreatedServiceName('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  // Auto-query Google Places using provider name when draft step mounts
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (
      step !== 'draft' ||
      autoQueryRanRef.current ||
      draft.source !== 'manual' ||
      !draft.name.trim() ||
      draft.addressLine1.trim() ||
      draft.city.trim() ||
      draft.state.trim() ||
      draft.postalCode.trim()
    ) {
      return;
    }

    autoQueryRanRef.current = true;
    setIsAutoQueryLoading(true);

    triggerAddressAutocomplete({
      q: draft.name.trim(),
      sessionToken: addressSessionTokenRef.current,
      centerLat,
      centerLng,
      radiusM: 50000,
    })
      .unwrap()
      .then((result) => {
        setNameAutoQueryPredictions((result.predictions ?? []).slice(0, 3));
        setIsAutoQueryLoading(false);
      })
      .catch(() => {
        setNameAutoQueryPredictions([]);
        setIsAutoQueryLoading(false);
      });
  }, [step, draft.source, draft.name, draft.addressLine1, draft.city, draft.state, draft.postalCode, triggerAddressAutocomplete, centerLat, centerLng]);

  /** Handle picking an auto-query prediction card. */
  const handleAutoQueryPredictionPick = useCallback(
    async (prediction: GooglePlacesAutocompletePredictionV2) => {
      setSelectedAutoQueryPlaceId(prediction.place_id);

      try {
        const details = await googleDetails({
          placeId: prediction.place_id,
          sessionToken: addressSessionTokenRef.current,
        }).unwrap();

        // Rotate session token after details fetch
        addressSessionTokenRef.current = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

        setDraft((d) => ({
          ...d,
          googlePlaceId: details.place_id,
          formattedAddress: details.formatted_address ?? '',
          addressLine1: details.address_line1 ?? '',
          addressLine2: details.address_line2 ?? '',
          city: details.city ?? '',
          state: details.state ?? '',
          postalCode: details.postal_code ?? '',
          country: details.country ?? 'US',
          latitude: details.latitude ?? null,
          longitude: details.longitude ?? null,
        }));
      } catch {
        toast.error('Unable to load address details');
        setSelectedAutoQueryPlaceId(null);
      }
    },
    [googleDetails],
  );

  // Determine whether to show auto-query cards vs manual address fields
  const addressIsPreFilled =
    draft.source !== 'manual' ||
    !!draft.addressLine1.trim() ||
    !!draft.city.trim() ||
    !!draft.state.trim() ||
    !!draft.postalCode.trim();

  // Show radio cards when: manual source, auto-query has results or is loading, not clicked "Enter manually", and no address prefilled yet
  // Exception: if the user already selected a card (address got filled), keep showing cards
  const showAutoQueryCards =
    draft.source === 'manual' &&
    !showManualAddressEntry &&
    (isAutoQueryLoading ||
      nameAutoQueryPredictions.length > 0) &&
    (!addressIsPreFilled || selectedAutoQueryPlaceId !== null);

  // ---------------------------------------------------------------------------
  // Visible items (6 default)
  // ---------------------------------------------------------------------------
  const internalVisible = useMemo(
    () => (showAllInternal ? search.resolveMatches : search.resolveMatches.slice(0, 6)),
    [search.resolveMatches, showAllInternal],
  );
  const reviewVisible = useMemo(
    () => (showAllReview ? search.reviewResults : search.reviewResults.slice(0, 6)),
    [search.reviewResults, showAllReview],
  );
  const googleVisible = useMemo(
    () => (showAllGoogle ? search.googleResults : search.googleResults.slice(0, 6)),
    [search.googleResults, showAllGoogle],
  );

  // ---------------------------------------------------------------------------
  // Handlers — search step
  // ---------------------------------------------------------------------------

  const handleInternalPick = useCallback((match: ResolveProviderMatch) => {
    setDraft({
      source: 'internal',
      baseProviderServiceId: match.provider_service_id,
      googlePlaceId: null,
      name: match.name,
      formattedAddress: match.formatted_address ?? '',
      addressLine1: match.address_line1 ?? '',
      addressLine2: match.address_line2 ?? '',
      city: match.city ?? '',
      state: match.state ?? '',
      postalCode: match.postal_code ?? '',
      country: match.country ?? 'US',
      latitude: null,
      longitude: null,
      phone: match.phone ?? '',
      fax: '',
      website: '',
    });
    setProviderType(null);
    setTreatmentStatus(null);
    setCareLevel(null);
    setSelectedReviewForApproval(null);
    setStep('draft');
  }, []);

  const handleGooglePick = useCallback(
    async (prediction: GooglePlacesAutocompletePredictionV2) => {
      try {
        const details = await googleDetails({
          placeId: prediction.place_id,
          sessionToken: sessionTokenRef.current,
        }).unwrap();

        search.rotateGoogleSessionToken();

        setDraft({
          source: 'google',
          baseProviderServiceId: null,
          googlePlaceId: details.place_id,
          name: details.name ?? prediction.main_text ?? prediction.description,
          formattedAddress: details.formatted_address ?? '',
          addressLine1: details.address_line1 ?? '',
          addressLine2: details.address_line2 ?? '',
          city: details.city ?? '',
          state: details.state ?? '',
          postalCode: details.postal_code ?? '',
          country: details.country ?? 'US',
          latitude: details.latitude ?? null,
          longitude: details.longitude ?? null,
          phone: details.phone_number ?? '',
          fax: '',
          website: details.website ?? '',
        });
        setProviderType(null);
        setTreatmentStatus(null);
        setCareLevel(null);
        setSelectedReviewForApproval(null);
        setStep('draft');
      } catch {
        toast.error('Unable to load Google place details');
      }
    },
    [googleDetails, search],
  );

  const handleReviewPick = useCallback(
    (reviewProvider: ReviewFoundProviderEntry) => {
      const resolveFallback = reviewProvider.provider_service_ref_id
        ? search.resolveMatchesAll.find(
            (match) =>
              match.provider_service_id === reviewProvider.provider_service_ref_id,
          )
        : undefined;
      const mention = reviewProvider.latest_mention;
      const enrichment = mention?.enrichment;
      const mailingHandle = enrichment?.handles?.find(
        (handle) => handle.handle_type === 'MAILING_ADDRESS',
      );
      const phoneHandle = enrichment?.handles?.find(
        (handle) => handle.handle_type === 'PHONE',
      );
      const websiteHandle = enrichment?.handles?.find(
        (handle) => handle.handle_type === 'WEBSITE',
      );
      const reviewFormattedAddress =
        enrichment?.confirmed_address?.trim() ||
        mention?.location_hint?.trim() ||
        '';
      const parsedReviewAddress = parseAddressComponents(reviewFormattedAddress);

      setDraft({
        source: reviewProvider.provider_service_ref_id ? 'internal' : 'manual',
        baseProviderServiceId: reviewProvider.provider_service_ref_id ?? null,
        googlePlaceId: null,
        name: firstNonEmpty(
          reviewProvider.name,
          mention?.organization_name,
          resolveFallback?.name,
          search.query,
        ),
        formattedAddress: firstNonEmpty(
          reviewFormattedAddress,
          resolveFallback?.formatted_address,
        ),
        addressLine1: firstNonEmpty(
          mailingHandle?.address_line1,
          parsedReviewAddress.addressLine1,
          resolveFallback?.address_line1,
        ),
        addressLine2: firstNonEmpty(
          mailingHandle?.address_line2,
          resolveFallback?.address_line2,
        ),
        city: firstNonEmpty(
          mailingHandle?.city,
          parsedReviewAddress.city,
          resolveFallback?.city,
        ),
        state: firstNonEmpty(
          mailingHandle?.state,
          parsedReviewAddress.state,
          resolveFallback?.state,
        ),
        postalCode: firstNonEmpty(
          mailingHandle?.zip,
          parsedReviewAddress.postalCode,
          resolveFallback?.postal_code,
        ),
        country: resolveFallback?.country ?? 'US',
        latitude: null,
        longitude: null,
        phone: firstNonEmpty(phoneHandle?.value, resolveFallback?.phone),
        fax: '',
        website: firstNonEmpty(websiteHandle?.value),
      });
      setProviderType(null);
      setTreatmentStatus(null);
      setCareLevel(null);
      setSelectedReviewForApproval(reviewProvider);
      setStep('draft');
    },
    [search.query, search.resolveMatchesAll],
  );

  const handleGoToCreateNew = useCallback(() => {
    setDraft({ ...EMPTY_DRAFT, name: search.query });
    setProviderType(null);
    setTreatmentStatus(null);
    setCareLevel(null);
    setSelectedReviewForApproval(null);
    // Reset auto-query state so a fresh query fires for the new name
    autoQueryRanRef.current = false;
    setNameAutoQueryPredictions([]);
    setShowManualAddressEntry(false);
    setSelectedAutoQueryPlaceId(null);
    setStep('draft');
  }, [search.query]);

  // ---------------------------------------------------------------------------
  // Handlers — draft step
  // ---------------------------------------------------------------------------

  /** Handle address line 1 changes with debounced Google Places autocomplete. */
  const handleAddressLine1Change = useCallback(
    (value: string) => {
      setDraft((d) => ({ ...d, addressLine1: value }));

      if (addressTimerRef.current) {
        window.clearTimeout(addressTimerRef.current);
        addressTimerRef.current = null;
      }

      const trimmed = value.trim();
      if (trimmed.length < 3) {
        setAddressPredictions([]);
        setShowAddressPredictions(false);
        return;
      }

      addressTimerRef.current = window.setTimeout(async () => {
        try {
          const result = await triggerAddressAutocomplete({
            q: trimmed,
            sessionToken: addressSessionTokenRef.current,
            centerLat,
            centerLng,
            radiusM: 50000,
          }).unwrap();
          setAddressPredictions(result.predictions ?? []);
          setShowAddressPredictions((result.predictions ?? []).length > 0);
        } catch {
          setAddressPredictions([]);
          setShowAddressPredictions(false);
        }
      }, 300);
    },
    [triggerAddressAutocomplete, centerLat, centerLng],
  );

  /** Handle picking a Google Places prediction from the address autocomplete dropdown. */
  const handleAddressPredictionPick = useCallback(
    async (prediction: GooglePlacesAutocompletePredictionV2) => {
      setShowAddressPredictions(false);
      setAddressPredictions([]);

      try {
        const details = await googleDetails({
          placeId: prediction.place_id,
          sessionToken: addressSessionTokenRef.current,
        }).unwrap();

        // Rotate session token after details fetch
        addressSessionTokenRef.current = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

        setDraft((d) => ({
          ...d,
          googlePlaceId: details.place_id,
          formattedAddress: details.formatted_address ?? '',
          addressLine1: details.address_line1 ?? '',
          addressLine2: details.address_line2 ?? '',
          city: details.city ?? '',
          state: details.state ?? '',
          postalCode: details.postal_code ?? '',
          country: details.country ?? 'US',
          latitude: details.latitude ?? null,
          longitude: details.longitude ?? null,
        }));
      } catch {
        toast.error('Unable to load address details');
      }
    },
    [googleDetails],
  );

  /** Build the address payload shared by create and conflict-resolution calls. */
  const buildAddressPayload = useCallback(
    () => ({
      formatted_address: composeAddress(draft) || null,
      address_line1: draft.addressLine1.trim() || null,
      address_line2: draft.addressLine2.trim() || null,
      city: draft.city.trim() || null,
      state: draft.state.trim() || null,
      postal_code: draft.postalCode.trim() || null,
      country: draft.country.trim() || 'US',
      latitude: draft.latitude,
      longitude: draft.longitude,
      google_place_id: draft.googlePlaceId,
    }),
    [draft],
  );

  /** Derive the API source value from the draft source. */
  const apiSource = useMemo((): 'internal_existing' | 'google_place' | 'manual' => {
    if (draft.source === 'internal') return 'internal_existing';
    if (draft.source === 'google') return 'google_place';
    return 'manual';
  }, [draft.source]);

  /**
   * Process a successful create/reuse response.
   * Shows appropriate toast and advances to the template step.
   */
  const handleCreateSuccess = useCallback(
    async (
      providerServiceId: string,
      medicalProviderId: string | undefined,
      reusedExisting: boolean,
    ) => {
      setCreatedServiceId(providerServiceId);
      if (medicalProviderId) setCreatedMedicalProviderId(medicalProviderId);
      setCreatedServiceName(draft.name.trim());
      setLastReusedExisting(reusedExisting);

      if (matterId) {
        dispatch(
          medicalProvidersApiClient.util.invalidateTags([
            { type: 'MedicalProviders', id: matterId },
          ]),
        );

        // Patch the medical provider with treatment status and care level
        if (medicalProviderId && (treatmentStatus || careLevel)) {
          try {
            await updateMedicalProvider({
              matterId,
              providerId: medicalProviderId,
              provider: {
                ...(treatmentStatus && { treatment_status: treatmentStatus }),
                ...(careLevel !== null && { care_level: careLevel }),
              },
            }).unwrap();
          } catch {
            toast.error('Provider created but failed to set treatment details');
          }
        }
      }

      setStep('template');
    },
    [draft.name, matterId, treatmentStatus, careLevel, dispatch, updateMedicalProvider],
  );

  const handleCreateOrReuse = useCallback(async () => {
    if (!draft.name.trim()) {
      toast.error('Enter a provider name');
      return;
    }
    if (!providerType) {
      toast.error('Select provider type');
      return;
    }
    if (matterId && !treatmentStatus) {
      toast.error('Select treatment status');
      return;
    }
    if (matterId && !careLevel) {
      toast.error('Select treatment type');
      return;
    }
    const trimmedPhone = draft.phone.trim();
    if (trimmedPhone) {
      const phoneResult = validatePhoneNumber(trimmedPhone);
      if (!phoneResult.isValid) {
        setPhoneError(phoneResult.errorMessage ?? 'Invalid phone number');
        return;
      }
    }

    const addressPayload = buildAddressPayload();

    try {
      if (matterId) {
        const result = await createMatterProvider({
          providerDirectoryCreateServiceRequestV2: {
            matter_id: matterId,
            source: apiSource,
            base_provider_service_id: draft.baseProviderServiceId ?? null,
            service_name: draft.name.trim(),
            service_type: 'GENERAL',
            provider_type_hint: toProviderTypeHint(providerType),
            address: addressPayload,
            phone: draft.phone.trim() || null,
            fax: draft.fax.trim() || null,
            website: draft.website.trim() || null,
          },
        }).unwrap();

        await handleCreateSuccess(
          result.provider_service_id,
          result.medical_provider_id,
          result.reused_existing ?? false,
        );
      } else {
        const result = await createDirectoryProvider({
          providerDirectoryV2DirectoryCreateRequest: {
            source: apiSource,
            base_provider_service_id: draft.baseProviderServiceId ?? null,
            service_name: draft.name.trim(),
            service_type: 'GENERAL',
            address: addressPayload,
            phone: draft.phone.trim() || null,
            fax: draft.fax.trim() || null,
            website: draft.website.trim() || null,
          },
        }).unwrap();

        await handleCreateSuccess(
          result.provider_service_id,
          undefined,
          result.reused_existing ?? false,
        );
      }
    } catch (err) {
      if (isConflictError(err)) {
        setConflictCandidates(err.data.candidates);
        setSelectedCandidateId(err.data.candidates[0]?.provider_service_id ?? null);
        setCreateAnywayReason('');
        setShowCreateAnywayForm(false);
        setStep('conflict');
      } else {
        toast.error('Failed to create provider');
      }
    }
  }, [
    draft,
    providerType,
    treatmentStatus,
    careLevel,
    buildAddressPayload,
    apiSource,
    createMatterProvider,
    createDirectoryProvider,
    handleCreateSuccess,
    matterId,
  ]);

  // ---------------------------------------------------------------------------
  // Handlers — conflict resolution
  // ---------------------------------------------------------------------------

  /** User selects one of the duplicate candidates. */
  const handleConflictUseExisting = useCallback(async () => {
    if (!selectedCandidateId) {
      toast.error('Select a provider to use');
      return;
    }
    const trimmedPhone = draft.phone.trim();
    if (trimmedPhone) {
      const phoneResult = validatePhoneNumber(trimmedPhone);
      if (!phoneResult.isValid) {
        setPhoneError(phoneResult.errorMessage ?? 'Invalid phone number');
        return;
      }
    }

    const addressPayload = buildAddressPayload();

    try {
      if (matterId) {
        const result = await createMatterProvider({
          providerDirectoryCreateServiceRequestV2: {
            matter_id: matterId,
            source: apiSource,
            base_provider_service_id: draft.baseProviderServiceId ?? null,
            service_name: draft.name.trim(),
            service_type: 'GENERAL',
            provider_type_hint: providerType ? toProviderTypeHint(providerType) : undefined,
            resolution_mode: 'USE_EXISTING',
            selected_candidate_provider_service_id: selectedCandidateId,
            address: addressPayload,
            phone: draft.phone.trim() || null,
            fax: draft.fax.trim() || null,
            website: draft.website.trim() || null,
          },
        }).unwrap();

        await handleCreateSuccess(
          result.provider_service_id,
          result.medical_provider_id,
          true,
        );
      } else {
        const result = await createDirectoryProvider({
          providerDirectoryV2DirectoryCreateRequest: {
            source: apiSource,
            base_provider_service_id: draft.baseProviderServiceId ?? null,
            service_name: draft.name.trim(),
            service_type: 'GENERAL',
            resolution_mode: 'USE_EXISTING',
            selected_candidate_provider_service_id: selectedCandidateId,
            address: addressPayload,
            phone: draft.phone.trim() || null,
            fax: draft.fax.trim() || null,
            website: draft.website.trim() || null,
          },
        }).unwrap();

        await handleCreateSuccess(
          result.provider_service_id,
          undefined,
          true,
        );
      }
    } catch {
      toast.error('Failed to link to existing provider');
    }
  }, [
    selectedCandidateId,
    buildAddressPayload,
    apiSource,
    draft,
    providerType,
    createMatterProvider,
    createDirectoryProvider,
    handleCreateSuccess,
    matterId,
  ]);

  /** User explicitly creates a new provider despite duplicates. */
  const handleConflictCreateAnyway = useCallback(async () => {
    if (!createAnywayReason.trim()) {
      toast.error('Please provide a reason for creating a new provider');
      return;
    }
    const trimmedPhone = draft.phone.trim();
    if (trimmedPhone) {
      const phoneResult = validatePhoneNumber(trimmedPhone);
      if (!phoneResult.isValid) {
        setPhoneError(phoneResult.errorMessage ?? 'Invalid phone number');
        return;
      }
    }

    const addressPayload = buildAddressPayload();

    try {
      if (matterId) {
        const result = await createMatterProvider({
          providerDirectoryCreateServiceRequestV2: {
            matter_id: matterId,
            source: apiSource,
            base_provider_service_id: draft.baseProviderServiceId ?? null,
            service_name: draft.name.trim(),
            service_type: 'GENERAL',
            provider_type_hint: providerType ? toProviderTypeHint(providerType) : undefined,
            resolution_mode: 'CREATE_ANYWAY',
            create_anyway_reason: createAnywayReason.trim(),
            address: addressPayload,
            phone: draft.phone.trim() || null,
            fax: draft.fax.trim() || null,
            website: draft.website.trim() || null,
          },
        }).unwrap();

        await handleCreateSuccess(
          result.provider_service_id,
          result.medical_provider_id,
          false,
        );
      } else {
        const result = await createDirectoryProvider({
          providerDirectoryV2DirectoryCreateRequest: {
            source: apiSource,
            base_provider_service_id: draft.baseProviderServiceId ?? null,
            service_name: draft.name.trim(),
            service_type: 'GENERAL',
            resolution_mode: 'CREATE_ANYWAY',
            create_anyway_reason: createAnywayReason.trim(),
            address: addressPayload,
            phone: draft.phone.trim() || null,
            fax: draft.fax.trim() || null,
            website: draft.website.trim() || null,
          },
        }).unwrap();

        await handleCreateSuccess(
          result.provider_service_id,
          undefined,
          false,
        );
      }
    } catch {
      toast.error('Failed to create provider');
    }
  }, [
    createAnywayReason,
    buildAddressPayload,
    apiSource,
    draft,
    providerType,
    createMatterProvider,
    createDirectoryProvider,
    handleCreateSuccess,
    matterId,
  ]);

  // ---------------------------------------------------------------------------
  // Phone validation
  // ---------------------------------------------------------------------------

  const handlePhoneBlur = useCallback(() => {
    const trimmed = draft.phone.trim();
    if (!trimmed) {
      setPhoneError(null);
      return;
    }
    const result = validatePhoneNumber(trimmed);
    setPhoneError(result.isValid ? null : (result.errorMessage ?? 'Invalid phone number'));
  }, [draft.phone]);

  const handleApproveProvider = useCallback(async () => {
    if (!matterId || !selectedReviewForApproval) {
      return;
    }
    if (!draft.name.trim()) {
      toast.error('Enter a provider name');
      return;
    }
    if (!providerType) {
      toast.error('Select provider type');
      return;
    }
    if (!treatmentStatus) {
      toast.error('Select treatment status');
      return;
    }
    if (!careLevel) {
      toast.error('Select treatment type');
      return;
    }

    const formattedAddress = composeAddress(draft);

    try {
      const approvedProvider = await approveMedicalProvider({
        matterId,
        providerId: selectedReviewForApproval.medical_provider_id,
        provider: {
          provider_type_hint: toProviderTypeHint(providerType),
          name: draft.name.trim() || undefined,
          address: formattedAddress || undefined,
        },
      }).unwrap();

      // Patch treatment status and care level after approval
      const medProvId =
        approvedProvider.id ?? selectedReviewForApproval.medical_provider_id;
      if (treatmentStatus || careLevel) {
        try {
          await updateMedicalProvider({
            matterId,
            providerId: medProvId,
            provider: {
              ...(treatmentStatus && { treatment_status: treatmentStatus }),
              ...(careLevel !== null && { care_level: careLevel }),
            },
          }).unwrap();
        } catch {
          toast.error('Provider approved but failed to set treatment details');
        }
      }

      setCreatedMedicalProviderId(medProvId);
      setCreatedServiceId(
        approvedProvider.provider_service_ref_id ??
          selectedReviewForApproval.provider_service_ref_id ??
          null,
      );
      setCreatedServiceName(draft.name.trim());

      if (
        approvedProvider.provider_service_ref_id ??
        selectedReviewForApproval.provider_service_ref_id
      ) {
        setStep('template');
      } else {
        toast.success('Provider approved');
        onProviderCreated(
          selectedReviewForApproval.medical_provider_id,
          draft.name.trim() || undefined,
        );
        onClose();
      }
    } catch {
      toast.error('Failed to approve provider');
    }
  }, [
    matterId,
    selectedReviewForApproval,
    providerType,
    treatmentStatus,
    careLevel,
    draft,
    approveMedicalProvider,
    updateMedicalProvider,
    onProviderCreated,
    onClose,
  ]);

  const handleFinish = useCallback(() => {
    const id = matterId
      ? createdMedicalProviderId ?? createdServiceId ?? ''
      : createdServiceId ?? '';
    if (lastReusedExisting === true) {
      toast.success('Linked to existing provider');
    } else if (lastReusedExisting === false) {
      toast.success('Created new provider');
    } else {
      toast.success('Provider added');
    }
    onProviderCreated(id, createdServiceName || undefined);
    onClose();
  }, [
    createdMedicalProviderId,
    createdServiceId,
    createdServiceName,
    lastReusedExisting,
    matterId,
    onProviderCreated,
    onClose,
  ]);

  const handleBack = useCallback(() => {
    if (step === 'draft') {
      setSelectedReviewForApproval(null);
      // Reset auto-query state so it can re-fire if user returns to draft
      autoQueryRanRef.current = false;
      setNameAutoQueryPredictions([]);
      setShowManualAddressEntry(false);
      setSelectedAutoQueryPlaceId(null);
      setStep('search');
    } else if (step === 'conflict') {
      setStep('draft');
    }
  }, [step]);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const dialogTitleMap: Record<WizardStep, string> = {
    search: 'Add Provider',
    draft: selectedReviewForApproval
      ? 'Approve Provider'
      : draft.source === 'manual'
        ? 'Create New Provider'
        : 'Confirm Provider',
    conflict: 'Possible Duplicate Found',
    template: 'Configure Request Template',
  };
  const dialogTitle = dialogTitleMap[step];

  const hasSearchQuery = search.query.trim().length >= 2;
  const hideInternalSection =
    !isDirectoryMode && search.hasReviewResolveOverlap;
  const totalResults =
    (hideInternalSection ? 0 : search.resolveMatches.length) +
    search.reviewResults.length +
    search.googleResults.length;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      size={step === 'search' ? '4xl' : '2xl'}
      zIndex={Z_INDEX_MODAL_BASE}
    >
      <DialogTitle>
        <div className="flex items-center gap-2">
          {(step === 'draft' || step === 'conflict') && (
            <button
              type="button"
              onClick={handleBack}
              className="rounded p-1 hover:bg-zinc-100"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          {dialogTitle}
        </div>
        {step === 'template' && createdServiceName && (
          <div className="text-sm font-normal text-zinc-500 mt-0.5">
            {createdServiceName}
          </div>
        )}
      </DialogTitle>

      <DialogBody>
        {/* ----- Step 1: Search ----- */}
        {step === 'search' && (
          <div className="flex flex-col gap-4">
            <InputGroup>
              <Search data-slot="icon" />
              <Input
                value={search.query}
                onChange={(e) => search.setQuery(e.target.value)}
                placeholder="Search by provider name or address..."
                type="search"
                autoFocus
              />
            </InputGroup>

            <div className="border border-zinc-200 rounded-lg overflow-hidden">
              <div className="max-h-[50vh] overflow-auto">
                {!hasSearchQuery && (
                  <div className="p-6 text-center bg-zinc-50">
                    <Search className="mx-auto h-8 w-8 text-zinc-300 mb-2" />
                    <p className="text-sm text-zinc-500">
                      Start typing to search for providers
                    </p>
                  </div>
                )}

                {hasSearchQuery && (
                  <>
                    {!hideInternalSection && (
                      <>
                        {/* Internal results group */}
                        <SectionHeader
                          label="Internal results"
                          count={search.resolveMatches.length}
                          total={search.resolveMatches.length}
                          showAll={showAllInternal}
                          onToggle={() => setShowAllInternal((v) => !v)}
                        />
                        {search.resolveMatches.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-zinc-500 border-b border-zinc-100">
                            {search.isSearching ? 'Searching...' : 'No internal matches'}
                          </div>
                        ) : (
                          internalVisible.map((match) => (
                            <button
                              key={match.provider_service_id}
                              type="button"
                              onClick={() => handleInternalPick(match)}
                              className={`w-full text-left px-4 py-3 hover:bg-zinc-50 border-b border-zinc-100 ${
                                match.already_on_matter ? 'opacity-60' : ''
                              }`}
                            >
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium text-zinc-900 text-sm truncate">
                                  {match.name}
                                </span>
                                {match.has_preferred_records && (
                                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                                )}
                                {match.already_on_matter && (
                                  <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 flex-shrink-0">
                                    On matter
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-zinc-500 mt-0.5">
                                {match.subtitle ?? compactInternalLocation(match.formatted_address)}
                              </div>
                              {((match.verified_contact_count ?? 0) > 0 || (match.matter_link_count ?? 0) > 0) && (
                                <div className="flex items-center gap-3 mt-1">
                                  {(match.verified_contact_count ?? 0) > 0 && (
                                    <span className="flex items-center gap-1 text-xs text-emerald-700">
                                      <CheckCircle2 className="h-3 w-3" />
                                      {match.verified_contact_count} verified
                                    </span>
                                  )}
                                  {(match.matter_link_count ?? 0) > 0 && (
                                    <span className="flex items-center gap-1 text-xs text-zinc-500">
                                      <Link2 className="h-3 w-3" />
                                      {match.matter_link_count} matter{(match.matter_link_count ?? 0) > 1 ? 's' : ''}
                                    </span>
                                  )}
                                </div>
                              )}
                            </button>
                          ))
                        )}
                      </>
                    )}

                    {/* Review-found group (matter mode only) */}
                    {!isDirectoryMode && (
                      <>
                        <SectionHeader
                          label="Review found providers (this matter)"
                          count={search.reviewResults.length}
                          total={search.reviewResults.length}
                          showAll={showAllReview}
                          onToggle={() => setShowAllReview((v) => !v)}
                        />
                        {search.reviewResults.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-zinc-500 border-b border-zinc-100">
                            No review providers
                          </div>
                        ) : (
                          reviewVisible.map((p) => (
                            <button
                              key={p.medical_provider_id}
                              type="button"
                              onClick={() => handleReviewPick(p)}
                              className="w-full text-left px-4 py-3 hover:bg-zinc-50 border-b border-zinc-100"
                            >
                              <div className="font-medium text-zinc-900 text-sm truncate">
                                {p.name ?? 'Unspecified provider'}
                              </div>
                              {p.latest_mention?.location_hint && (
                                <div className="text-xs text-zinc-500 mt-0.5 truncate">
                                  {p.latest_mention.location_hint}
                                </div>
                              )}
                              <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">
                                {p.review_summary ||
                                  p.latest_mention?.reasoning ||
                                  'Needs approval'}
                              </div>
                            </button>
                          ))
                        )}
                      </>
                    )}

                    {/* Google results group */}
                    <SectionHeader
                      label="Google results"
                      count={search.googleResults.length}
                      total={search.googleResults.length}
                      showAll={showAllGoogle}
                      onToggle={() => setShowAllGoogle((v) => !v)}
                      suffix={
                        search.googleWasBroadened
                          ? 'No nearby matches — showing broader results'
                          : undefined
                      }
                    />
                    {search.googleResults.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-zinc-500">
                        {search.isSearching ? 'Searching...' : 'No Google matches'}
                      </div>
                    ) : (
                      googleVisible.map((p) => (
                        <button
                          key={p.place_id}
                          type="button"
                          onClick={() => handleGooglePick(p)}
                          disabled={isFetchingDetails}
                          className="w-full text-left px-4 py-3 hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0 disabled:opacity-50"
                        >
                          <div className="font-medium text-zinc-900 text-sm">
                            {p.main_text ?? p.description}
                          </div>
                          <div className="text-xs text-zinc-500 mt-0.5">
                            {compactLocation(p.secondary_text ?? '')}
                          </div>
                        </button>
                      ))
                    )}

                    {/* Create new action */}
                    {search.hasSearched && !search.isSearching && (
                      <div className="border-t border-zinc-200 p-4">
                        <button
                          type="button"
                          onClick={handleGoToCreateNew}
                          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          <Plus className="h-4 w-4" />
                          Create new provider manually
                        </button>
                      </div>
                    )}
                  </>
                )}

                {hasSearchQuery && search.isSearching && totalResults === 0 && (
                  <div className="p-6 text-center">
                    <Loader2 className="mx-auto h-6 w-6 text-zinc-400 animate-spin" />
                    <p className="text-sm text-zinc-500 mt-2">Searching...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ----- Step 2: Draft form ----- */}
        {step === 'draft' && (
          <div className="max-w-lg mx-auto space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-zinc-950">Provider name</div>
              {matterId && (
                <div className="flex items-center gap-2">
                  <Field className="min-w-0">
                    <Select
                      value={treatmentStatus ?? ''}
                      onChange={(e) =>
                        setTreatmentStatus(
                          (e.target.value as ProviderTreatmentStatus) || null,
                        )
                      }
                      invalid={!treatmentStatus}
                    >
                      <option value="" disabled>
                        Treatment Status *
                      </option>
                      {Object.values(ProviderTreatmentStatus)
                        .filter((s) => s !== ProviderTreatmentStatus.NEVER_TREATED)
                        .map((status) => (
                          <option key={status} value={status}>
                            {toTitleCase(status.replace(/_/g, ' '))}
                          </option>
                        ))}
                    </Select>
                  </Field>
                  <Field className="min-w-0">
                    <Select
                      value={careLevel ?? ''}
                      onChange={(e) =>
                        setCareLevel(
                          (e.target.value as TreatmentCareLevel) || null,
                        )
                      }
                      invalid={!careLevel}
                    >
                      <option value="" disabled>
                        Treatment Type *
                      </option>
                      {Object.values(TreatmentCareLevel).map((level) => (
                        <option key={level} value={level}>
                          {toTitleCase(level.replace(/_/g, ' '))}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
              )}
            </div>
            <Field>
              <Input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="e.g. Kaiser Permanente - Orthopedics"
                autoFocus
              />
            </Field>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-zinc-950">Address</div>
                {showAutoQueryCards && (
                  <button
                    type="button"
                    onClick={() => setShowManualAddressEntry(true)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    Enter manually
                  </button>
                )}
              </div>

              {showAutoQueryCards ? (
                <div className="space-y-2">
                  {isAutoQueryLoading ? (
                    <div className="flex items-center gap-2 py-3 text-sm text-zinc-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Looking up addresses...
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                        <Search className="h-3.5 w-3.5" />
                        Results for &ldquo;{draft.name.trim()}&rdquo;
                      </div>
                      <div className="space-y-2">
                        {nameAutoQueryPredictions.map((prediction) => {
                          const isSelected = selectedAutoQueryPlaceId === prediction.place_id;
                          return (
                            <button
                              key={prediction.place_id}
                              type="button"
                              onClick={() => handleAutoQueryPredictionPick(prediction)}
                              disabled={isFetchingDetails}
                              className={`w-full text-left rounded-lg border p-3 transition-colors ${
                                isSelected
                                  ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                                  : 'border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50'
                              } disabled:opacity-50`}
                            >
                              <div className="flex items-center gap-3">
                                <div
                                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                                    isSelected
                                      ? 'border-blue-600 bg-blue-600'
                                      : 'border-zinc-300'
                                  }`}
                                >
                                  {isSelected && (
                                    <div className="h-2 w-2 rounded-full bg-white" />
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-zinc-900 truncate">
                                    {prediction.main_text ?? prediction.description}
                                  </div>
                                  {prediction.secondary_text && (
                                    <div className="text-xs text-zinc-500 truncate">
                                      {prediction.secondary_text}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      value={draft.addressLine1}
                      onChange={(e) => handleAddressLine1Change(e.target.value)}
                      onFocus={() => {
                        if (addressPredictions.length > 0) setShowAddressPredictions(true);
                      }}
                      onBlur={() => {
                        // Delay hiding so click on prediction can fire first
                        setTimeout(() => setShowAddressPredictions(false), 200);
                      }}
                      placeholder="Street address"
                      autoComplete="off"
                    />
                    {showAddressPredictions && addressPredictions.length > 0 && (
                      <div className="absolute z-50 mt-1 w-full rounded-md border border-zinc-200 bg-white shadow-lg max-h-48 overflow-auto">
                        {addressPredictions.map((prediction) => (
                          <button
                            key={prediction.place_id}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleAddressPredictionPick(prediction)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0"
                          >
                            <div className="font-medium text-zinc-900 truncate">
                              {prediction.main_text ?? prediction.description}
                            </div>
                            {prediction.secondary_text && (
                              <div className="text-xs text-zinc-500 truncate">
                                {prediction.secondary_text}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Input
                    value={draft.addressLine2}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, addressLine2: e.target.value }))
                    }
                    placeholder="Apt / Suite (optional)"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      value={draft.city}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, city: e.target.value }))
                      }
                      placeholder="City"
                    />
                    <Input
                      value={draft.state}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, state: e.target.value }))
                      }
                      placeholder="State"
                    />
                    <Input
                      value={draft.postalCode}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, postalCode: e.target.value }))
                      }
                      placeholder="ZIP"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field>
                <Label>Phone (optional)</Label>
                <Input
                  value={draft.phone}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, phone: e.target.value }));
                    if (phoneError) setPhoneError(null);
                  }}
                  onBlur={handlePhoneBlur}
                  placeholder="(555) 123-4567"
                  invalid={!!phoneError}
                />
                {phoneError && (
                  <p className="text-xs text-red-600 mt-1">{phoneError}</p>
                )}
              </Field>
              <Field>
                <Label>Fax (optional)</Label>
                <Input
                  value={draft.fax}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, fax: e.target.value }))
                  }
                  placeholder="(555) 123-4567"
                />
              </Field>
            </div>

            <Field>
              <Label>Website (optional)</Label>
              <Input
                value={draft.website}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, website: e.target.value }))
                }
                placeholder="https://..."
              />
            </Field>

            <div className="pt-4 border-t border-zinc-200">
              <div className="text-sm font-medium text-zinc-900 mb-3">
                Provider type <span className="text-red-500">*</span>
              </div>
              <ProviderTypeSelector
                value={providerType}
                onChange={setProviderType}
              />
            </div>
          </div>
        )}

        {/* ----- Step 2b: Conflict resolution ----- */}
        {step === 'conflict' && (
          <div className="max-w-lg mx-auto space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  We found {conflictCandidates.length} existing provider{conflictCandidates.length > 1 ? 's' : ''} that may match.
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Select an existing provider to link to, or create a new one with a reason.
                </p>
              </div>
            </div>

            {!showCreateAnywayForm && (
              <div className="space-y-2">
                {conflictCandidates.map((candidate) => (
                  <button
                    key={candidate.provider_service_id}
                    type="button"
                    onClick={() => setSelectedCandidateId(candidate.provider_service_id)}
                    className={`w-full text-left rounded-lg border p-3 transition-colors ${
                      selectedCandidateId === candidate.provider_service_id
                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                        : 'border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm font-medium text-zinc-900">
                          {candidate.name}
                        </div>
                        {candidate.address_summary && (
                          <div className="flex items-center gap-1 text-xs text-zinc-500 mt-0.5">
                            <MapPin className="h-3 w-3" />
                            {candidate.address_summary}
                          </div>
                        )}
                        {candidate.organization_name && (
                          <div className="text-xs text-zinc-500 mt-0.5">
                            {candidate.organization_name}
                          </div>
                        )}
                      </div>
                      <span className="text-[10px] font-medium text-zinc-400 uppercase tracking-wider">
                        {candidate.match_source.replace(/_/g, ' ')}
                      </span>
                    </div>
                    {candidate.similarity_score != null && candidate.similarity_score < 1 && (
                      <div className="text-[11px] text-zinc-400 mt-1">
                        {Math.round(candidate.similarity_score * 100)}% name match
                      </div>
                    )}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => setShowCreateAnywayForm(true)}
                  className="w-full text-left rounded-lg border border-dashed border-zinc-300 p-3 text-sm text-zinc-500 hover:border-zinc-400 hover:text-zinc-700 transition-colors"
                >
                  None of these — create a new provider anyway
                </button>
              </div>
            )}

            {showCreateAnywayForm && (
              <div className="space-y-3">
                <Field>
                  <Label>Why are you creating a new provider?</Label>
                  <Textarea
                    value={createAnywayReason}
                    onChange={(e) => setCreateAnywayReason(e.target.value)}
                    placeholder="e.g. Different department, different location, etc."
                    rows={3}
                  />
                </Field>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateAnywayForm(false);
                    setSelectedCandidateId(conflictCandidates[0]?.provider_service_id ?? null);
                  }}
                  className="text-xs text-zinc-500 hover:text-zinc-700 underline"
                >
                  Go back to candidate list
                </button>
              </div>
            )}
          </div>
        )}

        {/* ----- Step 3: Request Template ----- */}
        {step === 'template' && createdServiceId && (
          <RequestTemplatePanel
            providerServiceId={createdServiceId}
            onSaved={() => {
              // Auto-save feedback is handled by the panel
            }}
          />
        )}
      </DialogBody>

      <DialogActions>
        <Button
          outline
          onClick={
            step === 'draft' || step === 'conflict' ? handleBack : onClose
          }
        >
          {step === 'draft' || step === 'conflict' ? 'Back' : 'Cancel'}
        </Button>

        {step === 'draft' && (
          <Button
            color="primary"
            onClick={
              selectedReviewForApproval
                ? handleApproveProvider
                : handleCreateOrReuse
            }
            disabled={
              !draft.name.trim() ||
              !providerType ||
              (!!matterId && !treatmentStatus) ||
              (!!matterId && !careLevel) ||
              !!phoneError ||
              isApprovingReviewProvider ||
              isCreatingMatterProvider ||
              isCreatingDirectoryProvider
            }
          >
            {selectedReviewForApproval && isApprovingReviewProvider ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                Approving...
              </>
            ) : isCreatingMatterProvider || isCreatingDirectoryProvider ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                Creating...
              </>
            ) : (
              selectedReviewForApproval ? 'Approve Provider' : 'Add Provider'
            )}
          </Button>
        )}

        {step === 'conflict' && !showCreateAnywayForm && (
          <Button
            color="primary"
            onClick={handleConflictUseExisting}
            disabled={
              !selectedCandidateId ||
              isCreatingMatterProvider ||
              isCreatingDirectoryProvider
            }
          >
            {isCreatingMatterProvider || isCreatingDirectoryProvider ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                Linking...
              </>
            ) : (
              'Use Selected Provider'
            )}
          </Button>
        )}

        {step === 'conflict' && showCreateAnywayForm && (
          <Button
            color="primary"
            onClick={handleConflictCreateAnyway}
            disabled={
              !createAnywayReason.trim() ||
              isCreatingMatterProvider ||
              isCreatingDirectoryProvider
            }
          >
            {isCreatingMatterProvider || isCreatingDirectoryProvider ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                Creating...
              </>
            ) : (
              'Create Anyway'
            )}
          </Button>
        )}

        {step === 'template' && (
          <Button color="primary" onClick={handleFinish}>
            {isDirectoryMode ? 'Done' : 'Add to matter'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Section header sub-component
// ---------------------------------------------------------------------------

const SectionHeader = ({
  label,
  count,
  total,
  showAll,
  onToggle,
  suffix,
}: {
  label: string;
  count: number;
  total: number;
  showAll: boolean;
  onToggle: () => void;
  suffix?: string;
}) => (
  <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-zinc-200 px-4 py-2">
    <div className="flex items-center justify-between">
      <div className="text-xs font-semibold text-zinc-700">
        {label}
        {count > 0 && (
          <span className="ml-1 text-zinc-500 font-normal">({count})</span>
        )}
        {suffix && (
          <span className="ml-2 text-[11px] text-zinc-500 font-normal">
            {suffix}
          </span>
        )}
      </div>
      {total > 6 && (
        <button
          type="button"
          className="text-xs text-zinc-500 hover:text-zinc-700 underline"
          onClick={onToggle}
        >
          {showAll ? 'Show less' : `Show all (${total})`}
        </button>
      )}
    </div>
  </div>
);
